import { Popover } from "@headlessui/react";
import { ChevronDownIcon, ExclamationCircleIcon, TrashIcon, XIcon } from "@heroicons/react/outline";
import { ComponentType, ForwardedRef, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useModal, WorkflowOnlyContext } from "../contexts";
import useRichTextfield from '../hooks/useRichTextfield';
import useUser from "../hooks/useUser";
import { AbstractEmail, Attachment, Email, TeamUser, User } from "../interfaces";
import { BLOCK_ICON_MAP, capitalize, EMAIL_REGEX } from "../utils";
import EmailEditorAlias from './EmailEditorAlias';
import EmailEditorAttachments from './EmailEditorAttachments';
import EmailEditorAttachmentsModal from './EmailEditorAttachmentsModal';
import EmailEditorSender from "./EmailEditorSender";
import EmailEditorToolbar from './EmailEditorToolbar';
import WorkflowBlockModalSave from './WorkflowModalSave';
import WorkflowModalCustomFormattingWarning from "./WorkflowModalCustomFormattingWarning";

type SaveState = "INITIAL" | "HAS_CHANGES" | "SAVING" | "SAVED";
type SendState = "INITIAL" | "SENDING" | "SENT";

export type AttachmentAction = "CREATE" | "UPDATE" | "DELETE";
export interface Ref {
  openAttachmentMenu: () => void;
  resetInitialEmail: (email: AbstractEmail, onSave: () => void) => boolean;
  triggerSend?: () => Promise<void>;
}

export interface CustomEmailComponentProps {
  sender: User;
  value: string;
  setValue: (value: string) => void;
  disabled?: boolean;
}

interface Props {
  initialEmail: AbstractEmail;
  updateEmail: (email: AbstractEmail) => Promise<AbstractEmail>;
  updateAttachment: (action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) => Promise<void>;
  updateSender?: (user: TeamUser) => Promise<void>;
  checkCanChangeEmailType?: (emailType: "GMAIL" | "OUTLOOK") => Promise<boolean>;
  sendEmail?: () => Promise<void>;
  onClose: () => void;
  customTo?: JSX.Element;
  customBodyComponent?: ComponentType<CustomEmailComponentProps>;
  isSent?: boolean;
  disabled?: boolean;
  title?: string;
  overrideEmailType?: string;
  overrideCustomFormatting?: boolean;
}

function EmailEditor(props: Props, ref: ForwardedRef<Ref>) {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { user } = useUser();

  const { initialEmail, updateEmail, updateAttachment, updateSender, checkCanChangeEmailType, sendEmail, onClose } = props;

  const { workflow } = useContext(WorkflowOnlyContext);

  const [email, setEmail] = useState<AbstractEmail>(props.initialEmail)
  const [attachments, setAttachments] = useState<Attachment[]>(props.initialEmail.attachments);
  const [sender, setSender] = useState<TeamUser | undefined>(props.initialEmail.sender);

  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  const [saveState, setSaveState] = useState<SaveState>("INITIAL");
  const [sendState, setSendState] = useState<SendState>("INITIAL");

  const richEditorRef = useRef<HTMLDivElement | null>(null);

  const { RichTextfield, ...editorMethods } = useRichTextfield({
    editorRef: richEditorRef,
    content: email?.emailContent ?? '',
    setContent: (value: string) => updateAbstractEmail('emailContent', value),
    disabled: props.disabled
  });

  const { resetEditorContent } = editorMethods;

  const validTo = !email.emailTo || email.emailTo.split(",").every(email => EMAIL_REGEX.test(email.trim()));
  const validCc = !email.emailCc || email.emailCc.split(",").every(email => EMAIL_REGEX.test(email.trim()));
  const validBcc = !email.emailBcc || email.emailBcc.split(",").every(email => EMAIL_REGEX.test(email.trim()));

  useEffect(() => {
    setShowCc(!!email.emailCc)
    setShowBcc(!!email.emailBcc)

  }, [email.emailCc, email.emailBcc]);

  function changeSender(sender: TeamUser) {
    setSaveState("HAS_CHANGES");
    setSender(sender);
  }

  function updateAbstractEmail(field: string, value: any) {
    //@ts-ignore
    if (initialEmail[field] !== value)
      setSaveState("HAS_CHANGES");

    setEmail(prev => ({ ...prev!, [field]: value }));
  }

  function toggleCustomFormatting(customFormatting: boolean, force = false) {
    if (!customFormatting && !force) {
      return openModal(<WorkflowModalCustomFormattingWarning onClear={() => toggleCustomFormatting(false, true)} />)
    }

    if (!customFormatting) {
      updateAbstractEmail("customFormatting", customFormatting);
      updateAbstractEmail("emailContent", "");
    }

    if (customFormatting) {
      resetInitialEmail({ ...email, customFormatting: true })
    }
  }

  const updateAttachments = useCallback((attachments: Array<Attachment>) => {
    setSaveState("HAS_CHANGES");
    setAttachments(attachments)
  }, [setAttachments, setSaveState]);

  const openAttachmentMenu = useCallback(() => {
    openModal(<EmailEditorAttachmentsModal workflow={workflow!} attachments={attachments!} updateAttachments={updateAttachments} showMicrosoft postSigningStage />)
  }, [workflow, attachments, updateAttachments, openModal]);

  const handleSave = useCallback(async () => {
    setSaveState("SAVING");

    const initialAttachmentsIds = initialEmail.attachments.map(a => a.id);
    const currentAttachmentsIds = attachments.map(a => a.id);

    let attachmentsToPost: Array<string> = [];
    for (const attachment of attachments) {
      if (!initialAttachmentsIds.includes(attachment.id))
        attachmentsToPost.push(attachment.id)
    }

    let attachmentsToRemove: Array<string> = [];
    for (const existing of initialEmail.attachments) {
      if (!currentAttachmentsIds.includes(existing.id))
        attachmentsToRemove.push(existing.id)
    }

    let attachmentsToModify: Array<string> = [];
    for (const attachment of attachments) {
      const existing = initialEmail.attachments.find(a => a.id === attachment.id)
      if (existing && attachment.sharingMode !== existing?.sharingMode)
        attachmentsToModify.push(existing.id);
    }

    let attachmentUpdates: Array<Promise<any>> = [];

    attachmentsToPost.forEach(async (attachmentId: string) => {
      const attachment = attachments.find(a => a.id === attachmentId)!
      attachmentUpdates.push(updateAttachment("CREATE", attachmentId, attachment));
    });

    attachmentsToRemove.forEach(async attachmentId => {
      attachmentUpdates.push(updateAttachment("DELETE", attachmentId));
    });

    attachmentsToModify.map(async (attachmentId: string) => {
      const attachment = attachments.find(a => a.id === attachmentId)!
      attachmentUpdates.push(updateAttachment("UPDATE", attachmentId, attachment));
    })

    if (updateSender && sender)
      await updateSender(sender);

    await Promise.all(attachmentUpdates);
    const updatedEmail = await updateEmail(email);

    setEmail(updatedEmail);
    setAttachments(updatedEmail.attachments);
    setSender(updatedEmail.sender);

    setSaveState("SAVED");
  }, [attachments, email, initialEmail.attachments, sender, updateAttachment, updateEmail, updateSender]);

  async function updateEmailType(emailType: "GMAIL" | "OUTLOOK") {
    if (!checkCanChangeEmailType)
      return;

    const canChange = await checkCanChangeEmailType(emailType);
    if (!canChange)
      return;

    setEmail(prev => ({ ...prev!, emailType }));
    setSaveState("HAS_CHANGES");
  }

  const resetInitialEmail = useCallback((email: AbstractEmail, onSave?: () => void) => {
    if (onSave && saveState === "HAS_CHANGES" && initialEmail.id !== email.id) {
      openModal(<WorkflowBlockModalSave onSave={() => handleSave().then(() => { onSave(); closeModal(); })} onDontSave={() => closeModal()} />);
      return false;
    }

    setEmail(email);
    setAttachments(email.attachments);
    setSender(email.sender);

    resetEditorContent(email.emailContent);

    setSaveState("INITIAL");
    setSendState("INITIAL");

    return true;

  }, [resetEditorContent, closeModal, handleSave, initialEmail, openModal, saveState]);

  const generateSendEmailHandler = useCallback(() => {
    if (!sendEmail || props.disabled)
      return undefined;

    if (email.sender?.id !== user?.id)
      return undefined;

    return async () => {
      setSendState("SENDING");

      if (saveState === "HAS_CHANGES")
        await handleSave();

      return sendEmail()
        .then(() => setSendState("SENT"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEmail, props.disabled, email.sender?.id, user?.id, saveState]);

  useImperativeHandle(ref, () => ({
    openAttachmentMenu,
    resetInitialEmail,
    triggerSend: generateSendEmailHandler(),
  }), [openAttachmentMenu, resetInitialEmail, generateSendEmailHandler]);

  function handleAttemptClose() {
    if (!props.disabled && saveState === "HAS_CHANGES")
      return openModal(<WorkflowBlockModalSave onSave={() => handleSave().then(() => onClose())} onDontSave={() => onClose()} />);

    onClose();
  }

  return (
    <div className="flex flex-col bg-white w-[800px] m-auto border border-gray-200 shadow rounded overflow-hidden">
      <div className='bg-gray-100 flex items-center gap-2 px-2 py-1 border-b border-gray-200'>
        <img className="h-4 w-4" src={BLOCK_ICON_MAP[props.overrideEmailType ?? email.emailType]} alt="Email Icon" />
        <div className='font-gilroy font-semibold text-gray-600 text-sm flex items-center'>
          {props.title ?? t("Email")}&nbsp;via&nbsp;
          {checkCanChangeEmailType &&
            <Popover className="relative">
              <Popover.Button className='flex items-center underline'>
                {capitalize(email.emailType)} <ChevronDownIcon className='w-4 h-4' />
              </Popover.Button>
              <Popover.Panel className="absolute w-28 left-0 top-6 flex flex-col bg-white text-gray-600 drop-shadow-lg rounded text-sm z-40">
                <Popover.Button className='font-gilroy font-semibold p-2 hover:bg-gray-50 cursor-pointer whitespace-nowrap text-left flex items-center gap-1' onClick={() => updateEmailType("GMAIL")}>
                  <img className="w-4 flex-shrink-0" src={BLOCK_ICON_MAP["GMAIL"]} alt="Gmail Icon" /> Gmail
                </Popover.Button>
                <Popover.Button className='font-gilroy font-semibold p-2 hover:bg-gray-50 cursor-pointer whitespace-nowrap text-left flex items-center gap-1' onClick={() => updateEmailType("OUTLOOK")}>
                  <img className="w-4 flex-shrink-0" src={BLOCK_ICON_MAP["OUTLOOK"]} alt="Outlook Icon" /> Outlook
                </Popover.Button>
              </Popover.Panel>
            </Popover>
          }
          {!checkCanChangeEmailType &&
            <>{capitalize(props.overrideEmailType ?? email.emailType)}</>
          }
        </div>
        <XIcon className="w-5 h-5 ml-auto text-gray-600 hover:text-red cursor-pointer" onClick={handleAttemptClose} />
      </div>

      <div className="flex flex-col h-full p-2 pb-0">
        {sender
          ? <EmailEditorSender sender={sender} senderAlias={email.senderAlias} onChange={changeSender} workflow={workflow!} disabled={props.disabled || !updateSender} />
          : <EmailEditorAlias emailConfig={email as Email} onChange={updateAbstractEmail} workflow={workflow!} disabled={props.disabled} />
        }

        <>
          <div className="flex items-center w-full relative mt-2 pr-14">
            <label className="w-20 font-gilroy font-medium text-gray-500 flex-shrink-0" htmlFor="email-to" onClick={() => document.getElementById("email-to")?.focus()}>To</label>
            {!!props.customTo
              ? props.customTo
              : (
                <input
                  value={email.emailTo}
                  onChange={e => updateAbstractEmail("emailTo", e.currentTarget.value)}
                  placeholder="Type email addresses directly or insert {{Template Tags}}"
                  className="w-full focus-visible:outline-none h-10 whitespace-nowrap overflow-hidden"
                  id="email-to"
                  disabled={props.disabled}
                />
              )
            }

            <div className="absolute right-0 flex justify-end text-gray-500 bg-white/90 pl-1">
              {(email.emailTo === "" || !validTo) &&
                <ExclamationCircleIcon className="w-4 h-4 mr-2 mt-0.5 text-yellow " />
              }
              {(!props.disabled && !showCc) && <button className="mr-2 font-semibold" onClick={() => setShowCc(true)} type="button">Cc</button>}
              {(!props.disabled && !showBcc) && <button className="font-semibold" onClick={() => setShowBcc(true)} type="button">Bcc</button>}
            </div>
          </div>

          {showCc &&
            <div className="flex items-center w-full">
              <label className="w-20 font-gilroy font-medium text-gray-500" htmlFor="email-cc" onClick={() => document.getElementById("email-cc")?.focus()}>Cc</label>
              <div className="flex w-full items-center">
                <input
                  value={email.emailCc}
                  id="email-cc"
                  onChange={e => updateAbstractEmail("emailCc", e.currentTarget.value)}
                  className="w-full focus-visible:outline-none h-10 whitespace-nowrap overflow-hidden ml-2"
                  disabled={props.disabled}
                />
                {(showCc && email.emailCc && !validCc) &&
                  <ExclamationCircleIcon className="w-4 h-4 mt-0.5 text-yellow flex-shrink-0" />
                }
                {!props.disabled &&
                  <TrashIcon className="w-6 h-6 mx-2 cursor-pointer text-gray-500 hover:text-red" onClick={() => { setShowCc(false); updateAbstractEmail("emailCc", ""); }} />
                }
              </div>
            </div>
          }

          {showBcc &&
            <div className="flex items-center w-full">
              <label className="w-20 font-gilroy font-medium text-gray-500" htmlFor="email-cc" onClick={() => document.getElementById("email-Bcc")?.focus()}>Bcc</label>
              <div className="flex w-full items-center">
                <input
                  value={email.emailBcc}
                  id="email-bcc"
                  onChange={e => updateAbstractEmail("emailBcc", e.currentTarget.value)}
                  className="w-full focus-visible:outline-none h-10 whitespace-nowrap overflow-hidden ml-2"
                  disabled={props.disabled}
                />
                {(showBcc && email.emailBcc && !validBcc) &&
                  <ExclamationCircleIcon className="w-4 h-4 mt-0.5 text-yellow flex-shrink-0" />
                }
                {!props.disabled &&
                  <TrashIcon className="w-6 h-6 mx-2 cursor-pointer text-gray-500 hover:text-red" onClick={() => { setShowBcc(false); updateAbstractEmail("emailBcc", ""); }} />
                }
              </div>
            </div>
          }
        </>

        <hr className="border-gray-400" />

        <div className="flex items-center w-full mt-2">
          <label className="w-20 font-gilroy font-medium text-gray-500" htmlFor="email-subject">Subject</label>
          <input
            value={email.emailSubject}
            onChange={e => updateAbstractEmail("emailSubject", e.currentTarget.value)}
            className="w-full focus-visible:outline-none h-10 whitespace-nowrap overflow-hidden ml-2"
            id='email-subject'
            disabled={props.disabled}
          />
          {email.emailSubject.length === 0 &&
            <ExclamationCircleIcon className="w-4 h-4 mr-2 mt-0.5 text-yellow flex-shrink-0" />
          }
        </div>

        <hr className="border-gray-400 pb-2" />

        <div className="flex flex-col relative">
          <div className="h-[340px] overflow-auto relative">
            {(!!props.customBodyComponent && !(props.overrideCustomFormatting ?? email.customFormatting))
              ? <props.customBodyComponent value={email.emailContent} setValue={(v) => updateAbstractEmail("emailContent", v)} sender={user!} disabled={props.disabled} />
              : RichTextfield
            }
            <EmailEditorAttachments attachments={attachments} updateAttachments={updateAttachments} preventEditing={props.disabled} />
          </div>
          <EmailEditorToolbar
            {...editorMethods}
            toggleAttachmentMenu={openAttachmentMenu}
            customFormatting={props.customBodyComponent ? (props.overrideCustomFormatting ?? email.customFormatting) : undefined}
            updateCustomFormating={(props.customBodyComponent && !props.overrideCustomFormatting) ? toggleCustomFormatting : undefined}
            disabled={props.disabled}
            sendState={props.isSent ? "SENT" : sendState}
            saveState={saveState}
            handleSave={handleSave}
            handleSend={generateSendEmailHandler()}
            containsCTA={email.emailContent.includes("id=\"cta-button\"")}
          />
        </div>
      </div>
    </div>
  );

}

export default forwardRef(EmailEditor);
